import React from 'react'
import { useBulkSubmissions } from '../BulkSubmissionContext'
import { useState, useCallback } from 'react'
import {
  Upload,
  X,
  FileIcon,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
} from 'lucide-react'
import StepIndicator from './StepIndicator'

interface UploadedFile {
  id: string
  name: string
  type: string
}

const FileUpload = () => {
  const { updateStep } = useBulkSubmissions()
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return FileImage
    if (type.startsWith('video/')) return FileVideo
    if (type.startsWith('audio/')) return FileAudio
    if (type.startsWith('text/')) return FileText
    return FileIcon
  }

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return

    const filesArray = Array.from(newFiles).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      type: file.type,
    }))

    setFiles((prev) => [...prev, ...filesArray])
  }, [])

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const goToNextStep = () => {
    setTimeout(() => {
      updateStep(2)
    }, 1000)
    console.log(files)
  }

  return (
    <div className='max-w-4xl mx-auto space-y-6'>
      {/* Header */}
      <div className='flex flex-col items-start justify-center text-xl font-semibold text-gray-900'>
        <StepIndicator currentStep={2} />
        <div className='w-full flex justify-end gap-2'>
          <button onClick={() => setFiles([])} className='button muted'>
            Remove Files
          </button>
          <button onClick={() => updateStep(0)} className='button muted'>
            Cancel
          </button>
          <button
            onClick={goToNextStep}
            disabled={files.length === 0}
            className='button primary'
          >
            Validate
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div className='bg-white rounded-lg p-4 shadow-sm space-y-6'>
        {/* File List */}
        {files.length > 0 && (
          <div className='grid grid-cols-1 gap-3 max-h-[calc(50vh)] overflow-y-auto'>
            {files.map((file) => {
              const FileTypeIcon = getFileIcon(file.type)
              return (
                <div
                  key={file.id}
                  className='flex items-center justify-between p-3 border rounded-lg'
                >
                  <div className='flex items-center gap-3'>
                    <div className='w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600'>
                      <FileTypeIcon className='w-5 h-5' />
                    </div>
                    <span className='text-sm text-gray-700'>{file.name}</span>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className='p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors'
                  >
                    <X className='w-4 h-4' />
                    <span className='sr-only'>Remove file</span>
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {/* Upload Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload')?.click()}
          className={`
              border-2 border-dashed rounded-lg p-8
              flex flex-col items-center justify-center gap-2
              cursor-pointer transition-colors
              ${isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300'}
            `}
        >
          <input
            id='file-upload'
            type='file'
            multiple
            className='hidden'
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Upload className='w-8 h-8 text-gray-400' />
          <p className='text-sm text-gray-500'>
            {isDragging ? 'Drop files here' : 'No Files Selected, Click To Upload'}
          </p>
          <p className='text-xs text-gray-400'>* Required only</p>
        </div>
      </div>
    </div>
  )
}

export default FileUpload
